/**
 * operators.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { DataType } from "../common/data/data-types";
import { TypedDataHolder } from "../common/data/typed-data";
import { FilterNode, SearchNode, SearchTerm } from "./search-node";
import moment = require("moment");

export type OperatorString = "in" | "=" | ">" | "<" | "≥" | "≤";
// NOTE: technically the server also lists "contains" and "contains key",
// but they are unsupported for now (in general we are discouraging indices on
// lists/sets/maps anyway).

export function isInequality (op : OperatorString) : boolean
{
    return [">", "<", "≥", "≤"].indexOf(op) > -1;
}

const DATE_OP_NAMES : Map<OperatorString, string> =
    new Map<OperatorString, string>([
        ["=", "on"],
        [">", "after"],
        ["≥", "on or after"],
        ["<", "before"],
        ["≤", "before or on"]
    ] as Array<[OperatorString, string]>);

const STRING_OP_NAMES : Map<OperatorString, string> =
    new Map<OperatorString, string>([
        ["=", "is exactly"],
        [">", "alphabetically after"],
        // ["≥", "on or after"],
        ["<", "alphabetically before"]
        // ["≤", "before or on"]
    ] as Array<[OperatorString, string]>);

const OPERATOR_LABELS : Map<DataType, Map<OperatorString, string>> =
    new Map<DataType, Map<OperatorString, string>>();
OPERATOR_LABELS.set("date", DATE_OP_NAMES);
OPERATOR_LABELS.set("string", STRING_OP_NAMES);

export class Operator
{
    public static supports (op : OperatorString, data : TypedDataHolder)
        : boolean
    {
        if (op === Operator.EQUALS)
        {
            return true;
        }
        if (isInequality(op) &&
            (!data.restrictions || data.restrictions.length === 0))
        {
            return true;
        }
        return false;
    }

    public static labelFor (
      op : OperatorString | MagicOperator<any>,
      data : TypedDataHolder)
        : string
    {
        if (op instanceof MagicOperator)
        {
          return op.label;
        }
        const interimMap : Map<string, string> = OPERATOR_LABELS.get(data.type);
        if (interimMap && interimMap.has(op))
        {
            return interimMap.get(op);
        }
        return op;
    }

    public static EQUALS : OperatorString = "=";
    public static GREATER_THAN : OperatorString = ">";
    public static LESS_THAN : OperatorString = "<";
    public static GREATER_THAN_OR_EQUAL : OperatorString = "≥";
    public static LESS_THAN_OR_EQUAL : OperatorString = "≤";
    public static IN : OperatorString = "in";
    // public static CONTAINS : OperatorString = "contains";
    // public static CONTAINS_KEY : OperatorString = "contains key";
}

export abstract class MagicOperator<A> extends Operator
{
  public readonly name : string;
  public readonly label : string;
  public readonly filterType : DataType;
  public readonly argumentType : DataType;
  public readonly options : A[];
  public abstract transform (name : string, option? : A) : SearchTerm;
}

export class PastXDays extends MagicOperator<number>
{
  public readonly name : string = "pastXdays";
  public readonly label : string = "past X calendar days";
  public readonly filterType : DataType = "date";
  public readonly argumentType : DataType = "whole number";

  public transform (name : string, days : number) : SearchTerm
  {
    return {
      name,
      operator: "≥",
      value: this.daysAgo(days),
      magicSource: [this, days]
    };
  }

  private daysAgo (x : number) : Date
  {
    if (!Number.isSafeInteger(x))
    {
      throw new Error("PastXDays received non-integer argument!");
    }
    return moment().subtract(x, "days").utc().startOf("day").toDate();
  }

  public static readonly SoleInstance : PastXDays = new PastXDays();
}

export const MAGIC_FOR : Map<DataType, Array<MagicOperator<any>>> = new Map([
  ["date", [PastXDays.SoleInstance]]
] as Array<[DataType, Array<MagicOperator<any>>]>);

export const MAGIC_OPS_BY_NAME : Map<string, MagicOperator<any>> = new Map([
  [PastXDays.name, PastXDays.SoleInstance]
]);



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/search/operators.ts